import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FormikProps } from 'formik';
import { GripVertical, InfoCircleFill } from 'react-bootstrap-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { BracketCellGroup } from '../../../../../utils/groupMatchesByBracketCell';
import { applyBracketCellMatchOrder } from '../../../../../utils/bracketCellReorder.util';
import {
  getBracketCellSupersededExplanation,
  isTiebreakerRequired,
} from '../../../../../utils/bracketCellSuperseded.util';
import { Court, MatchResult, Team } from '../../../../../types/tournament';
import { formatTeamCategoryLabel } from '../../../../../utils/tournamentCategoryTemplate';
import { ClassTypeEnum } from '../../../../../types/enums';
import { ClassValues } from '../../../TournamentClasses/components/ClassesTag/ClassesTag';
import { IMatch, PlayValues } from '../PlayTag/PlayTag';
import { Match } from '../Match/Match';

type BracketCellSortableMatchesProps = {
  group: BracketCellGroup;
  formikArg: FormikProps<PlayValues>;
  courts: Court[];
  currentClass: ClassValues | undefined;
  handleAddTeam: (matchId: string | number, fieldName: string) => void;
  handleAddPlayer: (
    teamId: string | number,
    fieldName: string,
    matchId: string | number,
  ) => void;
  handleOpenResultModal: (
    results?: MatchResult[],
    matchId?: number | string,
    homeTeam?: Team | null,
    awayTeam?: Team | null,
  ) => void;
  matchesMap: PlayValues['tournamentMatches'];
  variant: 'group' | 'elimination';
  hideTeamPickers?: boolean;
};

type SortableMatchRowProps = {
  match: IMatch;
  idx: number;
  count: number;
  isGroupVariant: boolean;
  cellMatches: IMatch[];
  children: React.ReactNode;
};

function SortableMatchRow({
  match,
  idx,
  count,
  isGroupVariant,
  cellMatches,
  children,
}: SortableMatchRowProps) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const sortId = String(match.id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const superseded = match.bracketCellMatchSuperseded === true;
  const category = match.tournamentCategory;
  const categoryLabel = category
    ? formatTeamCategoryLabel(t, category, category.name)
    : undefined;
  const supersededExplanation = superseded
    ? getBracketCellSupersededExplanation(cellMatches, match)
    : null;
  const tiebreakerRequired =
    match.isTiebreaker && !superseded && isTiebreakerRequired(cellMatches, match);

  return (
    <div ref={setNodeRef} style={style} className="mb-2">
      <div className="small text-muted mb-1 d-flex align-items-center gap-2 flex-wrap">
        <button
          type="button"
          className="btn btn-link btn-sm p-0 text-muted"
          aria-label={t(prefix + 'bracketCellDragHandle')}
          {...attributes}
          {...listeners}
        >
          <GripVertical size={16} />
        </button>
        <span>
          {t(prefix + 'bracketCellMatchIndex', {
            index: idx + 1,
            count,
          })}
          {isGroupVariant && categoryLabel ? <span className="ms-1">— {categoryLabel}</span> : null}
        </span>
        {match.isTiebreaker ? (
          <span
            className={`badge ${tiebreakerRequired ? 'bg-warning text-dark' : superseded ? 'bg-secondary' : 'bg-info'}`}
          >
            {tiebreakerRequired
              ? t(prefix + 'tiebreakerBadgeRequired')
              : superseded
                ? t(prefix + 'tiebreakerBadgeNotRequired')
                : t(prefix + 'tiebreakerBadge')}
          </span>
        ) : null}
        {superseded && !match.isTiebreaker ? (
          <span className="badge bg-secondary">{t(prefix + 'bracketCellMatchNotRequired')}</span>
        ) : null}
        {superseded && supersededExplanation ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`superseded-${match.id}`}>
                {supersededExplanation.kind === 'drawn'
                  ? t(prefix + 'bracketCellSupersededDrawnTooltip', {
                      homeWins: supersededExplanation.homeWins,
                      awayWins: supersededExplanation.awayWins,
                    })
                  : supersededExplanation.winnerName
                    ? t(prefix + 'bracketCellSupersededTooltip', {
                        winnerName: supersededExplanation.winnerName,
                        homeWins: supersededExplanation.homeWins,
                        awayWins: supersededExplanation.awayWins,
                        matchCount: supersededExplanation.matchCount,
                      })
                    : t(prefix + 'bracketCellMatchNotRequired')}
              </Tooltip>
            }
          >
            <InfoCircleFill
              className="text-muted"
              size={14}
              aria-label={
                supersededExplanation.kind === 'drawn'
                  ? t(prefix + 'bracketCellSupersededDrawnTooltip', {
                      homeWins: supersededExplanation.homeWins,
                      awayWins: supersededExplanation.awayWins,
                    })
                  : supersededExplanation.winnerName
                    ? t(prefix + 'bracketCellSupersededTooltip', {
                        winnerName: supersededExplanation.winnerName,
                        homeWins: supersededExplanation.homeWins,
                        awayWins: supersededExplanation.awayWins,
                        matchCount: supersededExplanation.matchCount,
                      })
                    : t(prefix + 'bracketCellMatchNotRequired')
              }
            />
          </OverlayTrigger>
        ) : null}
      </div>
      {children}
    </div>
  );
}

export const BracketCellSortableMatches: FC<BracketCellSortableMatchesProps> = ({
  group,
  formikArg,
  courts,
  currentClass,
  handleAddTeam,
  handleAddPlayer,
  handleOpenResultModal,
  matchesMap,
  variant,
  hideTeamPickers = false,
}) => {
  const primary = group.matches[0];
  const count = primary.bracketCellMatchCount ?? group.matches.length ?? 1;
  const isGroupVariant = variant === 'group';
  const useCategoryMatchName = currentClass?.type === ClassTypeEnum.TEAMS;

  const sortedMatches = useMemo(
    () =>
      [...group.matches].sort(
        (a, b) => (a.bracketCellMatchIndex ?? 0) - (b.bracketCellMatchIndex ?? 0),
      ),
    [group.matches],
  );

  const [localOrder, setLocalOrder] = useState(() => sortedMatches.map((m) => String(m.id)));

  useEffect(() => {
    setLocalOrder(sortedMatches.map((m) => String(m.id)));
  }, [sortedMatches]);

  const orderedMatches = localOrder
    .map((id) => sortedMatches.find((m) => String(m.id) === id))
    .filter((m): m is IMatch => m != null);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (!over || active.id === over.id) {
          return;
        }
        const oldIndex = localOrder.indexOf(String(active.id));
        const newIndex = localOrder.indexOf(String(over.id));
        if (oldIndex === -1 || newIndex === -1) {
          return;
        }
        const nextOrder = arrayMove(localOrder, oldIndex, newIndex);
        setLocalOrder(nextOrder);
        const orderedIds = nextOrder
          .map((id) => orderedMatches.find((m) => String(m.id) === id)?.id)
          .filter((id): id is string | number => id != null);
        formikArg.setFieldValue(
          'tournamentMatches',
          applyBracketCellMatchOrder(formikArg.values.tournamentMatches, orderedIds),
        );
      }}
    >
      <SortableContext items={localOrder} strategy={verticalListSortingStrategy}>
        {orderedMatches.map((match, idx) => {
          const superseded = match.bracketCellMatchSuperseded === true;
          return (
            <SortableMatchRow
              key={match.id}
              match={match}
              idx={idx}
              count={count}
              isGroupVariant={isGroupVariant}
              cellMatches={orderedMatches}
            >
              <Match
                index={idx}
                matches={matchesMap}
                match={match}
                currentClass={currentClass}
                courts={courts}
                formikArg={formikArg}
                handleAddTeam={handleAddTeam}
                handleAddPlayer={handleAddPlayer}
                handleOpenResultModal={handleOpenResultModal}
                superseded={superseded}
                isGroup={isGroupVariant}
                sortVs={useCategoryMatchName}
                bracketCellChild
                hideTeamPickers={hideTeamPickers}
              />
            </SortableMatchRow>
          );
        })}
      </SortableContext>
    </DndContext>
  );
};
